import type { AdminInfo, WorkspaceMember } from '@protoss/shared';
import { createProjectRequestSchema, updateProjectRequestSchema } from '@protoss/shared';
import { Button, MultiSelect, Select, SelectItem, TextArea, TextField } from '@protoss/ui';
import * as React from 'react';
import { useDirtyState } from '../../hooks/useDirtyState';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

interface DomainOption {
  uuid: string;
  name: string;
  /** Domain admins - used to seed inherited locks in create mode */
  admins?: AdminInfo[];
}

interface ProjectFormProps {
  mode: 'create' | 'edit';
  domains: DomainOption[];
  preselectedDomainUuid?: string;
  /** All workspace members available for admin selection */
  workspaceMembers?: WorkspaceMember[];
  /**
   * Admins already persisted in the database - passed in edit mode for status tracking.
   * In create mode, omit this prop.
   */
  initialAdmins?: Array<{ uuid: string; displayName: string | null; email: string }>;
  initialData?: {
    name: string;
    description?: string | null;
    domainUuid?: string;
  };
  onSubmit: (data: {
    domainUuid?: string;
    name?: string;
    description?: string;
    projectAdminUuids?: string[];
  }) => Promise<void>;
  onCancel: () => void;
}

/**
 * ProjectForm - reusable form for creating and editing projects
 *
 * Features:
 * - Domain selection dropdown (create mode only)
 * - Client-side validation using Zod schemas
 * - Inline error display
 * - Create and edit modes
 * - Loading state during submission
 *
 * Usage:
 * ```tsx
 * // Create mode
 * <ProjectForm
 *   mode="create"
 *   domains={[{ uuid: '...', name: 'Engineering' }]}
 *   preselectedDomainUuid="domain-uuid"
 *   onSubmit={handleCreate}
 *   onCancel={handleCancel}
 * />
 *
 * // Edit mode
 * <ProjectForm
 *   mode="edit"
 *   domains={[]}
 *   initialData={{ name: 'Project A', description: 'Description', domainUuid: '...' }}
 *   onSubmit={handleUpdate}
 *   onCancel={handleCancel}
 * />
 * ```
 */
export function ProjectForm({
  mode,
  domains,
  preselectedDomainUuid,
  workspaceMembers = [],
  initialAdmins,
  initialData,
  onSubmit,
  onCancel,
}: ProjectFormProps) {
  const [domainUuid, setDomainUuid] = React.useState(
    initialData?.domainUuid ?? preselectedDomainUuid ?? ''
  );
  const [name, setName] = React.useState(initialData?.name ?? '');
  const [description, setDescription] = React.useState(initialData?.description ?? '');
  const [selectedAdminUuids, setSelectedAdminUuids] = React.useState<string[]>(
    () => initialAdmins?.map((a) => a.uuid) ?? []
  );
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    domainUuid: string;
    name: string;
    description: string;
    adminUuids: string[];
  }>({ trimStrings: true });

  const baselinePayload = React.useMemo(
    () => ({
      domainUuid: initialData?.domainUuid ?? preselectedDomainUuid ?? '',
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
      adminUuids: [...(initialAdmins?.map((a) => a.uuid) ?? [])].sort(),
    }),
    [
      initialAdmins,
      initialData?.description,
      initialData?.domainUuid,
      initialData?.name,
      preselectedDomainUuid,
    ]
  );

  const currentPayload = React.useMemo(
    () => ({
      domainUuid,
      name,
      description,
      adminUuids: [...selectedAdminUuids].sort(),
    }),
    [description, domainUuid, name, selectedAdminUuids]
  );

  React.useEffect(() => {
    setBaseline(baselinePayload);
  }, [baselinePayload, setBaseline]);

  React.useEffect(() => {
    updateCurrent(currentPayload);
  }, [currentPayload, updateCurrent]);

  useUnsavedChanges(!isSubmitting && isDirty);

  const schema = mode === 'create' ? createProjectRequestSchema : updateProjectRequestSchema;

  // Derive inherited (locked) admin UUIDs from the currently selected domain.
  // These are seeded from the domain's existing admin list and shown as non-removable.
  const domainAdminMap = React.useMemo(() => {
    const map = new Map<string, AdminInfo[]>();
    for (const d of domains) {
      map.set(d.uuid, d.admins ?? []);
    }
    return map;
  }, [domains]);

  const inheritedAdmins: AdminInfo[] = domainAdminMap.get(domainUuid) ?? [];
  const inheritedAdminUuids = inheritedAdmins.map((a) => a.uuid);

  // When domain changes in create mode, clear any extra admins the user had picked
  // so stale selections from the previous domain don't carry over.
  const handleDomainChange = (uuid: string) => {
    setDomainUuid(uuid);
    setSelectedAdminUuids([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Client-side validation
    const data =
      mode === 'create'
        ? {
            domainUuid,
            name,
            description: description || undefined,
            ...(selectedAdminUuids.length > 0 ? { projectAdminUuids: selectedAdminUuids } : {}),
          }
        : {
            name,
            description: description || undefined,
            ...(selectedAdminUuids.length > 0 ? { projectAdminUuids: selectedAdminUuids } : {}),
          };

    const result = schema.safeParse(data);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    // Submit
    try {
      setIsSubmitting(true);
      await onSubmit(result.data as Parameters<typeof onSubmit>[0]);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Submission failed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="project-form" onSubmit={handleSubmit}>
      <div className="project-form__fields">
        {mode === 'create' && (
          <div className="project-form__field">
            <label htmlFor="domain-select" className="project-form__label">
              Domain
            </label>
            <Select
              value={domainUuid}
              onValueChange={handleDomainChange}
              placeholder="Select a domain"
              disabled={isSubmitting}
              required
            >
              {domains.map((domain) => (
                <SelectItem key={domain.uuid} value={domain.uuid}>
                  {domain.name}
                </SelectItem>
              ))}
            </Select>
            {errors.domainUuid && (
              <p className="project-form__field-error" role="alert">
                {errors.domainUuid}
              </p>
            )}
          </div>
        )}

        <TextField
          label="Project Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Website Redesign, Q1 Campaign"
          error={errors.name}
          disabled={isSubmitting}
          required={mode === 'create'}
        />

        <TextArea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description of this project"
          error={errors.description}
          disabled={isSubmitting}
          rows={4}
        />

        {mode === 'edit' && (
          /* Always render so field height is stable before and after member fetch.
             Pre-seed options from initialAdmins so status list is immediate. */
          <MultiSelect
            options={
              workspaceMembers.length > 0
                ? workspaceMembers.map((m) => ({
                    value: m.uuid,
                    label: m.displayName ?? m.email,
                    sublabel: m.displayName ? m.email : undefined,
                  }))
                : (initialAdmins ?? []).map((a) => ({
                    value: a.uuid,
                    label: a.displayName ?? a.email,
                    sublabel: a.displayName ? a.email : undefined,
                  }))
            }
            selectedValues={selectedAdminUuids}
            originalValues={initialAdmins?.map((a) => a.uuid)}
            onSelectionChange={setSelectedAdminUuids}
            label="Project Administrators"
            placeholder="Type to search members…"
            loading={workspaceMembers.length === 0 && !initialAdmins?.length}
            disabled={isSubmitting}
            error={errors.projectAdminUuids}
          />
        )}

        {mode === 'create' && (
          /* Show inherited domain admins as locked chips plus a selector for additional admins. */
          <MultiSelect
            options={workspaceMembers.map((m) => ({
              value: m.uuid,
              label: m.displayName ?? m.email,
              sublabel: m.displayName ? m.email : undefined,
            }))}
            selectedValues={selectedAdminUuids}
            lockedValues={inheritedAdminUuids}
            onSelectionChange={setSelectedAdminUuids}
            label="Project Administrators"
            placeholder={
              workspaceMembers.length === 0 ? 'Loading…' : 'Add additional administrators…'
            }
            loading={workspaceMembers.length === 0 && inheritedAdminUuids.length === 0}
            disabled={isSubmitting}
            error={errors.projectAdminUuids}
          />
        )}
      </div>

      {errors.submit && (
        <div className="project-form__error" role="alert">
          {errors.submit}
        </div>
      )}

      <div className="project-form__actions">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting
            ? mode === 'create'
              ? 'Creating...'
              : 'Saving...'
            : mode === 'create'
              ? 'Create Project'
              : 'Save Changes'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
